<template>
  <div class="confirm-device__container">
    <div class="confirm-device__close-icon">
      <div @click.prevent="closeConfirmDeviceModal">
        <IconCross
          style="cursor: pointer"
          color="var(--color-primary)"
          size="25px"
        />
      </div>
    </div>
    <div class="confirm-device__body">
      <div class="confirm-device__device-title" style="font-weight: bold">
        {{ $t('product_form.confirm_device.are_you_sure_device') }}
      </div>
      <div v-if="descriptionText" class="confirm-device__description">
        {{ descriptionText }}
      </div>
      <div class="confirm-device__device">
        {{ $t('product_form.confirm_device.selected_device') }}
        <div class="confirm-device__name">
          {{ selectedDevice && selectedDevice.name }}
        </div>
      </div>
      <div v-if="descriptionImage" class="confirm-device__image-container">
        <div v-if="!imageLoaded">
          <logo-loading class="confirm-device__loading-logo" />
        </div>
        <transition name="fade">
          <img
            v-show="imageLoaded"
            class="confirm-device__image"
            :class="{ zoomed: isZoomed, horizontal: isHorizontal }"
            :src="descriptionImage"
            :alt="descriptionAlt"
            @load="onImageLoad"
            @touchstart="handleTouch"
          />
        </transition>
        <div class="confirm-device__double-click">
          {{ $t('product_form.confirm_device.double_click') }}
        </div>
      </div>
      <div class="confirm-device__description">
        {{ $t('product_form.confirm_device.tip') }}
      </div>
      <div class="confirm-device__actions">
        <button class="confirm-device__back" @click="returnToSelectDevice">
          {{ $t('product_form.confirm_device.negative_answer_device') }}
        </button>
        <button class="confirm-device__next" @click="confirmDeviceModal">
          {{ $t('product_form.confirm_device.positive_answer_device') }}
        </button>
      </div>
      <div
        class="confirm-device__discover-device"
        @click.prevent="showExplainPhoneType"
      >
        {{ $t('product_form.confirm_device.dont_know_my_device') }}
      </div>
    </div>
  </div>
</template>

<script>
import IconCross from '~/components/icons/cross/Cross.vue'
import LogoLoading from '~/components/loadings/logo'
import pushDataLayer from '~/mixins/gtm/push-data-layer'

export default {
  components: {
    IconCross,
    LogoLoading,
  },
  mixins: [pushDataLayer],
  props: {
    selectedDevice: {
      type: Object,
      default: () => {},
    },
    selectedBrand: {
      type: Object,
      default: () => {},
    },
    relatedDescription: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      imageLoaded: false,
      isZoomed: false,
      lastTouch: 0,
      isHorizontal: false,
    }
  },
  computed: {
    descriptionText() {
      return this.extractFromHtml(this.relatedDescription, 'p', 'textContent')
    },
    descriptionImage() {
      return this.extractFromHtml(this.relatedDescription, 'img', 'src')
    },
    descriptionAlt() {
      return this.extractFromHtml(this.relatedDescription, 'img', 'alt')
    },
  },
  watch: {
    descriptionImage(newSrc) {
      this.imageLoaded = false
    },
  },
  methods: {
    showExplainPhoneType() {
      this.$emit('showExplainPhoneType')
      this.pushDataLayer({
        event: 'genericEvent',
        eventCategory: 'navigation',
        eventAction: 'showExplainPhoneType',
        eventValue: {
          device: this.selectedDevice?.name,
          brand: this.selectedBrand?.name,
        },
      })
    },
    confirmDeviceModal() {
      this.$emit('confirmDeviceModal')
      this.pushDataLayer({
        event: 'genericEvent',
        eventCategory: 'navigation',
        eventAction: 'confirmDeviceModal',
        eventValue: {
          device: this.selectedDevice?.name,
          brand: this.selectedBrand?.name,
        },
      })
    },
    returnToSelectDevice() {
      this.$emit('returnToSelectDevice')
      this.pushDataLayer({
        event: 'genericEvent',
        eventCategory: 'navigation',
        eventAction: 'returnToSelectDevice',
        eventValue: {
          device: this.selectedDevice?.name,
          brand: this.selectedBrand?.name,
        },
      })
    },
    closeConfirmDeviceModal() {
      this.$emit('closeConfirmDeviceModal')
    },
    extractFromHtml(htmlString, selector, attribute) {
      const parser = new DOMParser()
      const doc = parser.parseFromString(htmlString, 'text/html')
      const element = doc.querySelector(selector)
      return element ? element[attribute] : ''
    },
    onImageLoad(event) {
      this.imageLoaded = true
      this.isHorizontal = event.target.naturalWidth > event.target.naturalHeight
    },
    toggleZoom() {
      this.isZoomed = !this.isZoomed
    },
    handleTouch(event) {
      const currentTime = new Date().getTime()
      const tapLength = currentTime - this.lastTouch
      if (tapLength < 500 && tapLength > 0) {
        this.toggleZoom()
      }
      this.lastTouch = currentTime
    },
  },
}
</script>

<style lang="scss">
$color-primary: #f37053;
$color-background: #fcfdf7;
$color-text-primary: #525252;
$color-text-secondary: #a1a1a1;
$color-white: #fff;
$color-shadow: rgba(0, 0, 0, 0.2);

:root {
  --color-primary: #{$color-primary};
}

.confirm-device {
  &__close-icon {
    display: flex;
    justify-content: flex-end;
    cursor: pointer;
  }
  &__container {
    display: flex;
    justify-content: flex-end;
    flex-direction: column;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: $color-background;
    border-radius: 7px;
    padding: 20px;
    z-index: 104;
    width: 100%;
    max-width: 350px;
    box-shadow: 0 0 10px 0 $color-shadow;
  }
  &__body {
    font-size: 17px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  &__title {
    text-transform: uppercase;
    font-size: 13px;
    font-weight: 700;
    text-align: center;
  }
  &__subtitle {
    font-size: 15px;
    font-weight: 400;
    text-align: center;
  }
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: $color-primary;
    color: $color-white;
    padding: 10px 20px;
    border-top-left-radius: 7px;
    border-top-right-radius: 7px;
  }
  &__device {
    padding-top: 10px;
    display: block;
    text-align: center;
    font-size: 15px;
  }
  &__device-title {
    padding-top: 10px;
    display: block;
    text-align: center;
    font-size: 20px;
  }
  &__description {
    padding-top: 10px;
    text-align: center;
    color: $color-text-primary;
    font-size: 15px;
  }
  &__double-click {
    display: none;
  }
  &__image-container {
    margin-top: 15px;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
  }
  &__image {
    border-radius: 5px;
    height: 300px;
    object-fit: contain;
    margin-top: auto;
    scale: 1;
    transition: transform 0.3s ease;
  }
  &__image.horizontal {
    height: 200px;
  }
  &__loading-logo {
    height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  &__back,
  &__next,
  &__start {
    flex: 1;
    text-align: center;
    padding: 10px;
    border-radius: 5px;
    border: none;
    cursor: pointer;
    width: 80%;
  }
  &__next {
    background-color: transparent;
    border: 1px solid $color-primary;
    font-weight: bold;
    color: $color-primary;
  }
  &__back {
    background-color: $color-primary;
    color: $color-white;
    font-weight: bold;
  }
  &__name {
    font-weight: bold;
  }
  &__actions {
    margin-top: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 10px;
  }
  &__discover-device {
    padding-top: 20px;
    font-size: 14px;
    color: $color-primary;
    text-decoration: underline;
    text-align: center;
    cursor: pointer;
  }
}
@media (min-width: $screen_tablet) {
  .confirm-device {
    &__container {
      max-width: 420px;
    }
    &__title {
      font-size: 16px;
    }
    &__subtitle {
      font-size: 20px;
      margin: 14px 0px 25px 0px;
    }
    &__actions {
      flex-direction: row;
    }
  }
}
@media (max-width: $v4_desktop) {
  .confirm-device {
    &__image {
      border-radius: 5px;
      height: 180px;
      object-fit: contain;
      margin-top: auto;
      scale: 1;
    }
  }
}
@media (max-width: $screen_tablet) {
  .confirm-device {
    &__image {
      border-radius: 5px;
      height: 150px;
      object-fit: contain;
      margin-top: auto;
      scale: 1;
    }
    &__image.horizontal {
      height: 150px;
    }
    &__image.zoomed {
      transform: scale(2.2);
    }
    &__double-click {
      display: flex;
      padding-top: 15px;
      text-align: center;
      color: $color-text-secondary;
      font-size: 10px;
      font-weight: bold;
    }
    &__image.horizontal.zoomed {
      transform: scale(1.2);
    }
  }
}
</style>
